import React, { memo, useEffect, useRef, useState } from "react";
import { classNames } from "helpers";
import { VehicleIntroSummaryProps } from "./types";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import carAnimation from "ui/theme/animations/invoice.json";
import VehicleCard from "../VehicleCard/VehicleCard";

/** Intro summary — prikazuje Lottie animaciju dok vozilo nije odabrano,
 *  a nakon odabira prikazuje VehicleCard s animiranim pojavljivanjem */
const VehicleIntroSummary: React.FC<VehicleIntroSummaryProps> = ({
  vehicleInfo,
  stepCurrent,
}) => {
  const thisStep = 0;
  const isActive = stepCurrent === thisStep;

  const containerRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [inView, setInView] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  /** Provjerava je li korisnik odabrao tip vozila */
  const hasVehicle = !!vehicleInfo.vehicleTypeName;

  /** Odgođeno aktivira animaciju pojave kartice vozila kad se vozilo odabere.
   *  Timeout od 50ms osigurava da se CSS tranzicija primijeni nakon renderiranja. */
  useEffect(() => {
    if (hasVehicle && !cardVisible) {
      const timer = setTimeout(() => setCardVisible(true), 50);
      return () => clearTimeout(timer);
    }
    if (!hasVehicle) setCardVisible(false);
  }, [hasVehicle]);

  /** Prati vidljivost kontejnera (IntersectionObserver) — pokreće animacije
   *  tek kad je komponenta vidljiva na ekranu (80% vidljivosti). */
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.8 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  /** Pokreće Lottie animaciju kad je komponenta vidljiva i vozilo još nije odabrano */
  useEffect(() => {
    if (inView && lottieRef.current && !hasVehicle) {
      lottieRef.current.setSpeed(0.5);
      lottieRef.current.goToAndPlay(0);
    }
  }, [inView, hasVehicle]);

  /** Zaustavlja Lottie animaciju na zadnjem frame-u nakon prvog loop-a */
  const handleLoopComplete = () => {
    if (lottieRef.current) {
      lottieRef.current.stop();
      const totalFrames = lottieRef.current.getDuration(true) ?? 0;
      if (totalFrames > 0) {
        lottieRef.current.goToAndStop(totalFrames - 1, true);
      }
    }
  };

  if (!isActive) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Stanje 1: Vozilo je odabrano — prikaži karticu s animacijom */}
      {hasVehicle ? (
        <>
          <VehicleCard
            vehicleInfo={vehicleInfo}
            isActive={isActive}
            isCompleted={false}
            step={stepCurrent + 1}
            title="Odabir vozila"
            className={classNames(
              `bg-white rounded-md shadow-xl w-[300px] flex-shrink-0 snap-start mb-12
               transition-all duration-700 ease-out`,
              cardVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-95"
            )}
          />
          <div
            className={classNames(
              "text-center transition-all duration-700 ease-out delay-300 max-w-md",
              cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <h1 className="text-4xl font-bold mb-3 text-white">
              Kreirajte svoje vozilo u par koraka
            </h1>
            <p className="text-lg opacity-80 text-blue-50">
              Vodimo te korak po korak kroz unos podataka o vozilu. Pripremite osnovne informacije i krenimo!
            </p>
          </div>
        </>
      ) : (
        /* Stanje 2: Vozilo nije odabrano — prikaži Lottie animaciju */
        <>
          <Lottie
            lottieRef={lottieRef}
            animationData={carAnimation}
            autoplay={false}
            loop={true}
            onLoopComplete={handleLoopComplete}
            className={classNames(
              "w-[400px] h-auto transition-all duration-700 ease-out delay-150",
              inView ? "opacity-100 scale-100" : "opacity-0 scale-90"
            )}
          />
          <div
            className={classNames(
              "text-center transition-all duration-700 ease-out delay-500 max-w-md",
              inView ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-16"
            )}
          >
            <h1 className="text-4xl font-bold mb-3 text-white">
              Kreirajte svoje vozilo u par koraka
            </h1>
            <p className="text-lg opacity-80 text-blue-50">
              Odaberite marku, model i tip vozila kako biste započeli proces kreiranja.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(VehicleIntroSummary);
